import { FC, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Textarea, Button, RadioGroup } from '@/components/ui';
import { IndustryAutocomplete } from './IndustryAutocomplete';
import { step1Schema, type Step1Data as FormData } from '@/validations';

interface Step1ProductServiceProps {
  initialData?: Partial<FormData>;
  onNext: (data: FormData) => void;
  onBack?: () => void;
  isSubmitting?: boolean;
  skipReset?: boolean;
}

export const Step1ProductService: FC<Step1ProductServiceProps> = ({
  initialData,
  onNext,
  onBack,
  isSubmitting = false,
  skipReset = false,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(step1Schema),
    defaultValues: {
      productOrService: initialData?.productOrService || 'Service',
      industry: initialData?.industry || '',
      description: initialData?.description ?? '',
      serviceDescription: initialData?.serviceDescription ?? '',
    },
  });

  // Reset form when initialData changes, but only if it's actually different
  // This prevents resetting with stale data right after save
  const prevInitialDataRef = useRef(initialData);
  useEffect(() => {
    // Skip if skipReset flag is set (during submission)
    if (skipReset) return;
    
    // Skip if initialData hasn't actually changed (reference or deep comparison)
    const prevData = prevInitialDataRef.current;
    if (
      prevData?.productOrService === initialData?.productOrService &&
      prevData?.industry === initialData?.industry &&
      prevData?.description === initialData?.description &&
      prevData?.serviceDescription === initialData?.serviceDescription
    ) {
      return;
    }
    
    // Update ref before resetting
    prevInitialDataRef.current = initialData;
    
    reset({
      productOrService: initialData?.productOrService || 'Service',
      industry: initialData?.industry || '',
      description: initialData?.description ?? '',
      serviceDescription: initialData?.serviceDescription ?? '',
    });
  }, [initialData, reset, skipReset]);

  const description = watch('description');
  const serviceDescription = watch('serviceDescription');

  const countWords = (text: string) => {
    return text?.trim().split(/\s+/).filter(Boolean).length || 0;
  };

  return (
    <form onSubmit={handleSubmit(onNext)} className="space-y-6">
      {/* Header */}
      {onBack && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Product or Service</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Tell us what you offer and your industry
          </p>
        </div>
      )}

      {/* Product or Service */}
      <Controller
        name="productOrService"
        control={control}
        render={({ field }) => (
          <RadioGroup
            label="What do you offer?"
            value={field.value || 'Service'}
            onChange={field.onChange}
            options={[
              { value: 'Product', label: 'Product' },
              { value: 'Service', label: 'Service' },
            ]}
            inline
            error={errors.productOrService?.message}
          />
        )}
      />

      {/* Industry - Autocomplete */}
      <Controller
        name="industry"
        control={control}
        render={({ field }) => (
          <IndustryAutocomplete
            value={field.value || ''}
            onChange={(value) => {
              field.onChange(value);
              // Clear industryId if industry name changes manually
              setValue('industryId', undefined);
            }}
            onSelect={(industryId, industryName) => {
              setValue('industry', industryName);
              setValue('industryId', industryId);
            }}
            error={errors.industry?.message}
            required
          />
        )}
      />

      {/* Company Description */}
      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <Textarea
            {...field}
            label="Company Description"
            optional
            rows={4}
            placeholder="Tell us about your company..."
            fullWidth
            error={errors.description?.message}
            showWordCount
            maxWords={300}
            currentWords={countWords(description || '')}
          />
        )}
      />

      {/* Service Description */}
      <Controller
        name="serviceDescription"
        control={control}
        render={({ field }) => (
          <Textarea
            {...field}
            label="Service Description"
            optional
            rows={4}
            placeholder="Describe your services..."
            fullWidth
            error={errors.serviceDescription?.message}
            showWordCount
            maxWords={300}
            currentWords={countWords(serviceDescription || '')}
          />
        )}
      />

      {/* Actions */}
      <div className="flex gap-3 pt-6 mt-6 border-t border-slate-200 justify-end">
        {onBack && (
          <Button type="button" variant="secondary" onClick={onBack}>
            Back
          </Button>
        )}
        <Button type="submit" variant="primary" isLoading={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
};
